import { createFileRoute, redirect } from "@tanstack/react-router";
import { dehydrate, DehydratedState, hydrate, useQuery, useQueryClient } from "@tanstack/react-query";
import { organisationCallscreeningQueryOptions, organisationQueryOptions } from "@/server/organisations";
import { CallScreening } from "@/components/call-screening";
import { useEffect } from "react";
import { checkIfUserMayWrite, CrudEntry } from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/callscreening/organisations/$orgId")({
  component: RouteComponent,
  beforeLoad: ({ location, params }) => {
    const search = (location as any).search || {};
    if (!search.tab) {
      throw redirect({
        to: "/callscreening/organisations/$orgId",
        params: { orgId: params.orgId },
        replace: true,
        search: { ...search, tab: "rules" },
      });
    }
  },
  loader: async ({ context, params }) => {
    const queryOptions = organisationQueryOptions(Number(params.orgId));
    await context.queryClient.prefetchQuery(queryOptions);
    const organisation = context.queryClient.getQueryData(queryOptions.queryKey) as { name?: string } | undefined;
    return {
      crumb: organisation?.name,
      dehydratedState: dehydrate(context.queryClient) as any,
      userProfileId: (context as { user?: { profile: string } }).user?.profile,
      accessControl: (context as { user?: { accessControl: Record<string, CrudEntry> } }).user?.accessControl,
    };
  },
});

function RouteComponent() {
  const { orgId } = Route.useParams();
  const { dehydratedState, userProfileId, accessControl } = Route.useLoaderData() as {
    dehydratedState: DehydratedState;
    userProfileId?: string;
    accessControl?: Record<string, CrudEntry>;
  };
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "CALL_SCREENING");
  const queryClient = useQueryClient();
  hydrate(queryClient, dehydratedState);
  const organisationQuery = useQuery(organisationQueryOptions(Number(orgId)));
  const organisation = organisationQuery.data as { name?: string } | undefined;
  const organisationCallscreeningQuery = useQuery(organisationCallscreeningQueryOptions(Number(orgId)));
  const organisationCallscreeningData = organisationCallscreeningQuery.data as
    | {
        incoming?: { modify: string; black: string; white: string };
        outgoing?: { modify: string; black: string; white: string };
      }
    | undefined;

  useEffect(() => {
    if (!orgId) return;
    void organisationQuery.refetch();
    void organisationCallscreeningQuery.refetch();
  }, [orgId, organisationQuery.refetch, organisationCallscreeningQuery.refetch]);

  return (
    <CallScreening
      resourceId={Number(orgId)}
      callscreeningData={organisationCallscreeningData}
      resourceType="organisation"
      loading={organisationCallscreeningQuery.isLoading}
      mayWrite={mayWrite}
    />
  );
}
